import { ClockTypes } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslation } from "react-i18next";

import type { Report } from "~/types";
import { useUser } from "~/hooks/use-user";

const fontSize = 12;

const addLine = (doc: jsPDF, currentY: number): number => {
  currentY += 10; // Add some spacing before the line
  doc.line(40, currentY, 550, currentY); // Draw the line
  currentY += 20; // Add some spacing after the line
  return currentY;
};

const resetFont = (doc: jsPDF) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
};

export const useReportManager = () => {
  const { t } = useTranslation("common");
  const { user } = useUser();
  const generateSummary = async ({
    start,
    end,
    dayReports,
    monthReports,
  }: {
    start: Date;
    end: Date;
    dayReports: Report[];
    monthReports: Report[];
  }) => {
    const totalPayments = monthReports.reduce(
      (acc, curr) =>
        acc +
        Object.values(curr.payments).reduce(
          (acc, curr) => acc + curr.amount,
          0,
        ),
      0,
    );

    const totalTips = monthReports.reduce(
      (acc, curr) =>
        acc +
        Object.values(curr.payments).reduce((acc, curr) => acc + curr.tip, 0),
      0,
    );

    const totalRevenue = monthReports.reduce(
      (acc, curr) => acc + curr.revenue,
      0,
    );

    const combinedPayments: Record<string, Report["payments"][number]> = {};

    Object.values(monthReports).forEach(({ payments }) => {
      Object.entries(payments).forEach(
        ([method, { amount, tip, quantity }]) => {
          combinedPayments[method] ??= { amount: 0, tip: 0, quantity: 0 }; // Initialize if not present
          combinedPayments[method].amount += amount; // Safely add amount
          combinedPayments[method].tip += tip; // Safely add amount
          combinedPayments[method].quantity += quantity; // Safely add amount
        },
      );
    });

    const combinedVat: Record<string, Report["vat"][number]> = {};

    Object.values(monthReports).forEach(({ vat }) => {
      Object.entries(vat).forEach(([rate, value]) => {
        combinedVat[rate] ??= 0; // Initialize if not present
        combinedVat[rate] += value; // Initialize if not present
      });
    });

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });

    const lineHeight = fontSize * 1.4;
    let currentY = 40;
    doc.setFontSize(fontSize);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("MONTH SUMMARY REPORT", 40, currentY);

    const result = await fetch("/images/logo-3d.png");

    if (!result.ok) {
      console.error("Could not load logo for document.");
    } else {
      const blob = await result.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => {
        reader.onload = () => {
          const base64Image = reader.result;

          if (typeof base64Image === "string") {
            // Add the image to the PDF
            const pageWidth = 550; // Width in points
            const imageWidth = 77; // Image width in points
            const imageHeight = 63; // Image height in points

            const x = pageWidth - imageWidth; // 10 pt margin from the right

            doc.addImage(base64Image, "PNG", x, 10, imageWidth, imageHeight); // Adjust coordinates and size
          }

          resolve(true);
        };
      });
    }

    resetFont(doc);

    currentY += lineHeight;

    doc.text(
      t("Report.from_until", {
        from: format(
          start,
          user.clock === ClockTypes.military ? "dd-MM-yyyy" : "MM/dd/yyyy",
        ),
        until: format(
          end,
          user.clock === ClockTypes.military ? "dd-MM-yyyy" : "MM/dd/yyyy",
        ),
      }),
      40,
      currentY,
    );
    currentY += 20;
    currentY = addLine(doc, currentY);
    currentY += lineHeight;

    doc.text(user.companyName ?? "", 40, currentY);
    currentY += lineHeight;
    doc.text(`${user.streetName} ${user.streetNumber}`, 40, currentY);
    currentY += lineHeight;
    doc.text(`${user.zipCode} ${user.city}, ${user.country}`, 40, currentY);
    currentY += lineHeight;

    doc.setFont("helvetica", "normal");

    currentY = addLine(doc, currentY);

    doc.text(t("Report.revenue"), 40, currentY);
    currentY += lineHeight;
    doc.text(
      t("Report.total", {
        total: formatIntToCurrency(totalRevenue, false),
      }),
      40,
      currentY,
    );

    autoTable(doc, {
      startY: currentY + 20,
      head: [
        [
          t("Report.vat_rate"),
          t("Report.net_revenue"),
          t("Report.vat"),
          t("Report.gross_revenue"),
        ],
      ],
      body: Object.entries(combinedVat).map(([rate, value]) => {
        return [`${rate}%`, "", "", formatIntToCurrency(value, false)];
      }),
      theme: "grid",
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [0, 0, 0] },
    });

    currentY =
      (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable
        .finalY + 10;

    autoTable(doc, {
      startY: currentY + 20,
      head: [
        [
          t("Report.payment_method").toUpperCase(),
          t("Report.amount").toUpperCase(),
          t("Report.tips").toUpperCase(),
          t("Report.total_label").toUpperCase(),
        ],
      ],
      body: Object.entries(combinedPayments)
        .map(([method, data]) => {
          const total = data.amount + data.tip;

          return [
            `${method} (${data.quantity})`,
            formatIntToCurrency(data.amount, false),
            formatIntToCurrency(data.tip, false),
            formatIntToCurrency(total, false),
          ];
        })
        .concat([
          [
            t("Report.total_label").toUpperCase(),
            formatIntToCurrency(totalPayments, false),
            formatIntToCurrency(totalTips, false),
            formatIntToCurrency(totalPayments + totalTips, false),
          ],
        ]),
      theme: "grid",
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [0, 0, 0] },
      didParseCell: (data) => {
        if (data.row.index === data.table.body.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    currentY =
      (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable
        .finalY + 10;

    autoTable(doc, {
      startY: currentY + 20,
      head: [
        [
          t("Report.day_label").toUpperCase(),
          t("Report.tickets_label").toUpperCase(),
          t("Report.total_label").toUpperCase(),
          t("Report.vat_info_label").toUpperCase(),
        ],
      ],
      body: dayReports
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map((day) => [
          format(day.date, "dd-MM"),
          day.numberOfReceipts,
          formatIntToCurrency(day.revenue, false),
          "",
        ]),
      theme: "grid",
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      bodyStyles: { textColor: [0, 0, 0] },
    });

    doc.save(`report.pdf`);
  };

  return {
    generateReport: generateSummary,
  };
};
